import Loader from '@/components/Loaders/Loader';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import circleCheckmark from '@/images/CircleCheckmark.svg';
import plusCircle from '@/images/plusCircle.svg';
import { IDreamsResult } from '@/pages/editor/components/hooks/useCreateDream';
import { useAnalytics } from '@/services/analytics';
import { ImageType } from '@d-id/types';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useUpdateImage } from '../../hooks';

export const BoxContainer = styled.div<{ disabled: boolean; showhq?: boolean; state: AddingState }>`
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    position: relative;
    border-radius: 12px;
    font-weight: 800;
    aspect-ratio: 1;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border: ${({ state, theme }) => (state === AddingState.added ? `2px solid ${theme.colors.primary.main}` : 'none')};

    &::before {
        z-index: 1;
        content: 'HQ';
        display: ${({ showhq }) => (showhq ? 'flex' : 'none')};
        justify-content: center;
        font-size: 1.2rem;
        align-items: center;
        pointer-events: none;
        position: absolute;
        background-color: ${({ theme }) => theme.colors.primary.main};
        color: white;
        font-weight: 800;
        border-radius: 6px;
        top: 6px;
        right: 6px;
        width: 20px;
        height: 20px;
    }
`;

export const BoxInnerImage = styled.div<{ src: string }>`
    background: url(${({ src }) => src}) center no-repeat black;
    background-size: cover;
    position: relative;
    overflow: hidden;
    height: 100%;
    width: 100%;
`;

enum AddingState {
    add = 'add',
    added = 'added',
    loading = 'loading',
}

const StyledLoader = styled(Loader)`
    width: 20px;
`;

const AddToDreams = styled.div<{ show: boolean; state: AddingState }>`
    background-color: ${({ theme }) => theme.colors.black[100] + '73'}; // 73 is for 45% opacity
    display: ${({ show }) => (show ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    font-size: 14px;
    height: 100%;
    width: 100%;
    bottom: 0;
    color: ${({ theme, state }) => (state === AddingState.added ? theme.colors.primary.main : theme.colors.secondary[900])};

    justify-content: center;
    align-items: center;
    gap: 5px;
`;

function AddComponent({ addState }: { addState: AddingState }) {
    if (addState === AddingState.add) {
        return (
            <>
                <MaskedIcon src={plusCircle} size="24px" />
                Add Avatar
            </>
        );
    } else if (addState === AddingState.added) {
        return (
            <>
                <MaskedIcon src={circleCheckmark} size="24px" />
                Added
            </>
        );
    }

    return <StyledLoader color="white" />;
}

interface IAddDreamBoxProps {
    disabled?: boolean;
    src: string;
    dream: IDreamsResult[number];
}

export function AddDreamBox({ disabled = false, dream }: IAddDreamBoxProps) {
    const mixpanel = useAnalytics();
    const [show, setShow] = useState(false);
    const [addState, setAddState] = useState<AddingState>(AddingState.add);
    const { mutateAsync: updateImage, isLoading } = useUpdateImage(
        () => setAddState(AddingState.added),
        () => setAddState(AddingState.add)
    );

    async function handleClick() {
        if (addState === AddingState.add) {
            setAddState(AddingState.loading);
            await updateImage({
                presenterType: ImageType.Dreams,
                id: dream.id,
                createdAt: dream.createdAt,
                show: true,
                name: 'Untitled avatar',
            });
            mixpanel?.track('dreams-avatar-added', { event: 'click' });
        }
    }

    return (
        <BoxContainer
            disabled={disabled || isLoading || addState !== AddingState.add}
            onClick={handleClick}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
            state={addState}>
            <BoxInnerImage src={dream.url}>
                <AddToDreams show={show || addState !== AddingState.add} state={addState}>
                    <AddComponent addState={addState} />
                </AddToDreams>
            </BoxInnerImage>
        </BoxContainer>
    );
}
